import React, { Fragment } from 'react';
import Layout from '@components/Layout';
import { Grid, Divider, Col, Row } from '@components/Grid';
import Card from '@components/Card';
import styles from './styles.module.scss';
import Img from 'gatsby-image';
import { useStaticQuery, graphql } from 'gatsby';
import Slider from '@components/Slider';
import { Link } from '@reach/router';
import { getBasket, getProductDiscountPrice, getPublicProductDiscount } from '@helpers/store';
import Product from '@components/ProductCard';
import { useSelector } from 'react-redux';
import SEO from '@components/SEO';
import useClient from '@helpers/hooks/useClient';
import { dashCase } from '@helpers/index';

const Shop = ({
    data: {
        page: {
            frontmatter: { title, featuredProducts },
        },
        categories,
        allMerlinStoreProduct: { products },
    },
}) => {
    const settings = {
        adaptiveHeight: true,
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 4,
        centerMode: false,
        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
    };
    const client = useClient();
    const basket = useSelector(({ basket }) => basket);
    const basketProducts = client ? getBasket(products, basket) : [];

    // map featured products (array of product short names) with real product data
    featuredProducts = featuredProducts
        ?.map(({ productShortName, image }) => {
            const product = products?.find(({ shortName }) => shortName === productShortName);

            return product
                ? {
                      product,
                      image,
                  }
                : null;
        })
        ?.filter(x => !!x);

    return (
        <Layout>
            <SEO
                title={title}
                description="Here at Sylvan Arms, we are dedicated to developing and providing the highest quality gunsmithing products for the best possible price, check out our line."
            />
            <Grid>
                <div className={styles.storeHeadingWrapper}>
                    <div className={styles.storeHeading}>
                        <h1>{title}</h1>
                    </div>

                    <div className={styles.storeFeatured}>
                        <Slider autoplay>
                            {featuredProducts?.map(
                                ({ product: { _id, name, images, shortName }, image }, index) => (
                                    <Slider.Item key={_id}>
                                        <Link
                                            to={`/product/${shortName}`}
                                            className={styles.storeFeaturedItem}
                                        >
                                            <Row gutters={false}>
                                                <Col xs={12}>
                                                    <Fragment>
                                                        {(!!image?.childImageSharp?.fluid ||
                                                            !!images?.[0]?.image?.childImageSharp
                                                                ?.fluid) && (
                                                            <div
                                                                className={
                                                                    styles.storeFeaturedItemImage
                                                                }
                                                            >
                                                                <Img
                                                                    imgStyle={{
                                                                        objectFit: 'contain',
                                                                    }}
                                                                    fluid={
                                                                        image?.childImageSharp
                                                                            ?.fluid ??
                                                                        images[0].image
                                                                            .childImageSharp.fluid
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <h2>{name}</h2>
                                                    </Fragment>
                                                </Col>
                                            </Row>
                                        </Link>
                                    </Slider.Item>
                                )
                            )}
                        </Slider>
                    </div>
                </div>

                <Divider margin={5} />
                <Divider margin={5} lgMargin={0} />
                {categories.nodes
                    .filter(({ _id }) => products.some(product => product?.category?._id === _id))
                    .map(node => {
                        const filteredProducts = products.filter(
                            product => product?.category?._id === node._id
                        );

                        return (
                            <div key={node._id} className={styles.storeCategory}>
                                <Row>
                                    <Col xs={12}>
                                        <h2>{node.name}</h2>
                                        <Divider margin={2} />
                                        <div className={styles.storeCategoryProducts}>
                                            <Slider {...settings}>
                                                {filteredProducts.map(product => (
                                                    <Slider.Item key={product._id}>
                                                        <Product
                                                            product={product}
                                                            basket={basketProducts}
                                                        />
                                                    </Slider.Item>
                                                ))}
                                            </Slider>
                                        </div>
                                        <Divider margin={3} />
                                        <Row center="xs" middle="xs">
                                            <Col>
                                                <Link
                                                    style={{
                                                        textDecoration: 'none',
                                                        color: '#FFFFFF',
                                                    }}
                                                    to={`/category/${dashCase(node?.name)}`}
                                                >
                                                    <Card.Actions.Action
                                                        text={`View More ${node.name} `}
                                                    />
                                                </Link>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Divider margin={5} />
                                    <Divider margin={5} />
                                </Row>
                            </div>
                        );
                    })}
            </Grid>
        </Layout>
    );
};

export const query = graphql`
    query StoreQuery($path: String!) {
        page: markdownRemark(frontmatter: { path: { eq: $path } }) {
            frontmatter {
                title: store_title
                featuredProducts: store_featured_product {
                    productShortName: store_featured_product_short_name
                    image: store_featured_product_image {
                        childImageSharp {
                            fluid(maxWidth: 600) {
                                ...GatsbyImageSharpFluid
                            }
                        }
                    }
                }
            }
        }
        categories: allMerlinStoreCategory {
            nodes {
                _id
                name
            }
        }
        allMerlinStoreProduct {
            products: nodes {
                _id
                name
                category {
                    _id
                }
                shortName
                description
                price
                images {
                    image: url {
                        childImageSharp {
                            fluid(maxWidth: 600) {
                                ...GatsbyImageSharpFluid
                            }
                        }
                    }
                }
                discounts {
                    _id
                    type
                    code
                    type
                    amount
                    startDate
                    endDate
                    limit
                    products
                    categories
                    accountTypes
                }
            }
        }
    }
`;
export default Shop;
